/**
 * CallInformation DTO — mirrors the phoneservice /calls/callinformation response record.
 * All 24 fields are represented here. Nullable fields match the backend contract.
 * An optional 25th field, `customerId`, is set only by the single-call lookup endpoint (below).
 */
export interface CallInformation {
  timestamp: string;
  calledUserAccountId: number | null;
  callNoteUserAccountId: number | null;
  accepted: boolean;
  type: string;
  callingNumber: string;
  callingFullNumber: string;
  agentName: string;
  platzNumber: string;
  customerConnectSeconds: number;
  agentConnectSeconds: number;
  callDurationInSeconds: number;
  processDurationInSeconds: number;
  postProcessDurationInSeconds: number;
  status: string;
  vrId: string;
  cchsid: string;
  firstLoadBegin: string;
  processEndTime: string;
  callNoteId: number | null;
  brand: string;
  callerName: string;
  contactPerson: string;
  callReason: string;
  /**
   * Customer id for this call, resolved server-side. Only set by the single-call
   * endpoint `GET /calls/callinformation/{cchsid}` (EB-4154) — the list endpoint
   * (`findCallInformation`) never populates it, so it is not one of the 24 list
   * columns / `ALL_24_FIELD_NAMES`.
   * @ticket EB-4153
   */
  customerId?: string;
}

/**
 * Parameters for CallsService.findCallInformation().
 * All fields are optional — the service omits any missing/empty value from the HTTP request.
 * The UI enforces that userAccountId and phoneServiceId are mutually exclusive (never both set).
 */
export interface CallsQueryParams {
  /** Caller supplies Date instances; the service formats them to yyyy-MM-dd'T'HH:mm:ss.SSS (local time, no Z). */
  start?: Date;
  end?: Date;
  /** Optional customer ID (RAV ID). */
  customerId?: string;
  /** Optional numeric agent ID (employee number). */
  agentId?: number;
  /** Optional numeric user account ID — mutually exclusive with phoneServiceId. */
  userAccountId?: number;
  /** Optional numeric phone service ID — mutually exclusive with userAccountId. */
  phoneServiceId?: number;
  /** Optional called-number filter; sent verbatim as a string (may include `+`, digits, spaces). */
  calledNumber?: string;
}

/** Persisted column preferences (localStorage key cmt.calls.columns.v1). */
export interface ColumnPrefs {
  visibleFields: string[];
}

/**
 * Ordered list of all 24 CallInformation field names.
 * Used by AG-Grid CSV export to ensure every field is exported regardless of visibility.
 */
export const ALL_24_FIELD_NAMES: readonly (keyof CallInformation)[] = [
  'timestamp',
  'type',
  'status',
  'accepted',
  'callerName',
  'callingNumber',
  'callingFullNumber',
  'agentName',
  'platzNumber',
  'callDurationInSeconds',
  'callReason',
  'callNoteId',
  'contactPerson',
  'brand',
  'calledUserAccountId',
  'callNoteUserAccountId',
  'customerConnectSeconds',
  'agentConnectSeconds',
  'processDurationInSeconds',
  'postProcessDurationInSeconds',
  'firstLoadBegin',
  'processEndTime',
  'vrId',
  'cchsid',
];

/**
 * Human-readable labels for the raw `status` field on a `CallInformation` row.
 * Per CLAUDE.md Pre-Flight §10: never show raw API enum values to operators.
 *
 * The backend mixes cases (`BUSY`, `Notiz`, `Testanruf`, `vbOrphan`, …) so the
 * lookup is exact-match. Unknown values pass through via `callStatusLabel()`'s
 * fallback. Keep this in sync with the sub-type labels used by the Call Details
 * dialog (`call-note-details-tab.component.ts`).
 */
export const CALL_STATUS_LABELS: Readonly<Record<string, string>> = {
  BUSY: 'Busy',
  RING: 'Ringing',
  Notiz: 'Note',
  keiner: 'No outcome',
  Testanruf: 'Test call',
  vermittelt: 'Forwarded',
  vbOrphan: 'Voicebot abandoned',
  vbCompleted: 'Voicebot completed',
  vbHandoff: 'Voicebot → Agent',
};

/** Translate a raw call status to its display label; unknown values pass through. */
export function callStatusLabel(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '';
  return CALL_STATUS_LABELS[raw] ?? raw;
}

/**
 * Pill + dot tailwind classes for a call status / sub-type chip. Shared between
 * the Calls grid (statusCellRenderer) and the Call Details dialog so the same
 * value renders with the same color code in both places.
 *
 * The selection keys on the **raw** enum value (uppercased) — stable across UI
 * label changes. Unknown values fall through to a neutral muted pill.
 */
export interface CallStatusTone {
  /** Pill background + foreground (badge body). */
  pillClass: string;
  /** Indicator dot color. */
  dotClass: string;
}

export function callStatusTone(raw: string | null | undefined): CallStatusTone {
  const upper = (raw ?? '').toUpperCase();
  if (upper === 'BUSY') {
    return { pillClass: 'bg-destructive/15 text-destructive', dotClass: 'bg-destructive' };
  }
  if (upper === 'RING') {
    return { pillClass: 'bg-ring-purple/15 text-ring-purple', dotClass: 'bg-ring-purple' };
  }
  if (upper === 'NOTIZ') {
    return { pillClass: 'bg-primary/15 text-primary', dotClass: 'bg-primary' };
  }
  if (upper.startsWith('TEST')) {
    return { pillClass: 'bg-warning/15 text-warning', dotClass: 'bg-warning' };
  }
  // Voicebot outcomes — distinct hue from TEST* (warning/amber) so operators
  // can scan them apart at a glance. `accent` is the warm gold tone defined
  // in styles.tailwind.css; on dark themes it remains readable thanks to /20.
  if (upper.startsWith('VB')) {
    return { pillClass: 'bg-accent/25 text-accent-foreground', dotClass: 'bg-accent' };
  }
  return { pillClass: 'bg-muted text-muted-foreground', dotClass: 'bg-muted-foreground' };
}

/**
 * Default-visible columns — order mirrors the old CMT call list (Zeit / Knd A. / Knd CC /
 * Angen. / Typ / Anrufer / Angerufener / Mitarbeiter / Klingelzeit×2 / Gespr. /
 * Bearb. / Nachb. / Status) plus a compact CCHSID for support/Graylog correlation.
 * Status is intentionally last so the eye lands on the colored badge after scanning the
 * row left-to-right — same reading flow operators are used to. Callnote and Seat are
 * available via the Columns chooser but off by default (low information density on most
 * working sets).
 */
export const DEFAULT_VISIBLE_FIELDS: readonly (keyof CallInformation)[] = [
  'timestamp',
  'cchsid',
  'status',
  'accepted',
  'type',
  'calledUserAccountId',
  'callNoteUserAccountId',
  'callingNumber',
  'callingFullNumber',
  'agentName',
  'customerConnectSeconds',
  'agentConnectSeconds',
  'callDurationInSeconds',
  'processDurationInSeconds',
  'postProcessDurationInSeconds',
];

/** Toggleable columns that live in the column chooser (off by default). */
export const TOGGLEABLE_FIELDS: readonly (keyof CallInformation)[] = [
  'callNoteId',
  'platzNumber',
  'callerName',
  'callReason',
  'contactPerson',
  'brand',
  'firstLoadBegin',
  'processEndTime',
  'vrId',
];

/** Human-readable labels for toggleable columns — used by the column chooser UI. */
export const TOGGLEABLE_FIELD_LABELS: Readonly<Record<string, string>> = {
  callNoteId: 'Callnote ID',
  platzNumber: 'Seat',
  callerName: 'Caller Name',
  callReason: 'Call Reason',
  contactPerson: 'Contact Person',
  brand: 'Brand',
  firstLoadBegin: 'First Load Begin',
  processEndTime: 'Process End',
  vrId: 'VR-ID',
};

/** localStorage key for persisted column preferences (schema v1). */
export const COLUMN_PREFS_STORAGE_KEY = 'cmt.calls.columns.v1';
